import * as S from "./styles.ts";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import UserControl from "./UserControl";
import CartControl from "./CartControl";
import { Button, LoadingSpinner, UniversalLink } from "@template/components";
import useBreakpoint from "@/lib/helpers/hooks/useBreakpoint.ts";
import { userLoginStatus } from "@/lib/helpers/siteTracking/events.ts";
import { useEffect } from "react";

export interface IAuthProps {
  available_trial?: boolean;
  altStyling?: boolean;
  navLinks?: boolean;
  userControlLinks?: string;
}

const LogInPrompt = ({ available_trial, altStyling }: IAuthProps) => {
  const isSm = !useBreakpoint("md").check;

  return (
    <>
      <UniversalLink href="/login">
        <Button variant="outline">Log In</Button>
      </UniversalLink>
      {available_trial && !isSm && (
        <UniversalLink href="/voip/try-free/">
          <Button variant={altStyling ? "flat" : "solid"}>Try for Free</Button>
        </UniversalLink>
      )}
    </>
  );
};

const NavAuth = ({
  available_trial,
  altStyling,
  navLinks,
  userControlLinks,
}: IAuthProps) => {
  const { user, cart } = useTypedSelector((state) => state.user);
  const { plans, loaded } = useTypedSelector(
    (state) => state.store.plan_prices
  );

  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );
  const getSelectedPlan = () => {
    if (!user?.voip_plan || plans.length === 0) return undefined;

    return user.voip_plan === 8
      ? "Free Trial"
      : plans.find((plan) => plan.id === user.voip_plan)?.name;
  };

  const selectedPlan = getSelectedPlan();

  useEffect(() => {
    if (user?.loading || !loaded) return;

    userLoginStatus({
      loginStatus: user?.authenticated ? "logged_in" : "logged_out",
      userId: user?.id ?? null,
      plan: user?.has_voip ? selectedPlan : null,
      siteVersion: currency,
    });
  }, [
    user?.loading,
    loaded,
    user?.authenticated,
    user?.id,
    user?.has_voip,
    selectedPlan,
  ]);

  if (navLinks && userControlLinks === "Cart/Basket Only") {
    return (
      cart.items.length > 0 && (
        <S.AuthContainer>
          <CartControl altStyling={altStyling} />
        </S.AuthContainer>
      )
    );
  }

  return (
    <S.AuthContainer>
      {user.loading ? (
        <LoadingSpinner height={15} />
      ) : (
        <>
          {cart.items.length > 0 && <CartControl altStyling={altStyling} />}
          {user.authenticated ? (
            <UserControl />
          ) : (
            <LogInPrompt
              available_trial={available_trial}
              altStyling={altStyling}
            />
          )}
        </>
      )}
    </S.AuthContainer>
  );
};

export default NavAuth;
